/**
 * Tool — the canonical button primitive used on every UI surface (ToolPalette,
 * ToolbarStrip, HeaderBar, SidebarControls, ViewportCameraControls).
 *
 * A singular action is a `Tool` with no `variants`.
 * A grouped action — Blender / Photoshop "tool group" — is the same `Tool`
 * with `variants: ToolVariant[]`; it opens a flyout on click, on long-press /
 * drag, or on ArrowDown / Enter when focused.
 *
 * Sizing is locked to `TOOL_SIZE` on every surface. Flyouts open away from the
 * host surface via `ToolSurface` context (so a left-edge flyout opens right,
 * never over the strip).
 *
 * Keyboard:
 *  - Trigger: Enter / Space invokes `onActivate` (native button behavior).
 *    When the tool has variants, ArrowDown / Enter also opens the flyout.
 *  - Flyout:  ArrowUp / ArrowDown / Enter / Escape (via the shared Popover);
 *    single-character keys match a variant's `shortcut`.
 */

import { forwardRef, type ReactNode, useRef, useState } from "react";
import {
  COLOR_ACCENT,
  COLOR_ACTIVE_BG,
  COLOR_CAUTION,
  COLOR_CAUTION_BG,
  COLOR_HEADER_BG,
  COLOR_TEXT_DIM,
  COLOR_TEXT_DISABLED,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_ON_ACCENT,
  COLOR_TOOLBAR_HIGHLIGHT,
  RADIUS_SM,
  TOOL_SIZE,
  TOOLBAR_HIGHLIGHT_DURATION_MS,
} from "../tokens.ts";
import { ToolFlyout, type ToolVariant } from "./ToolFlyout.tsx";
import { flyoutPlacementForEdge, useToolSurface } from "./ToolSurface.tsx";
import { useToolGroupDrag } from "./useToolGroupDrag.ts";

export type ToolAccent = "default" | "caution";

/** Idle-state emphasis. `"cta"` paints the button as a call-to-action (solid
 *  accent bg + white fg) when `active` is false; the active state is unchanged. */
export type ToolEmphasis = "none" | "cta";

export type ToolProps = {
  /** Icon rendered inside the trigger (typically at TOOL_ICON_SIZE). */
  icon: ReactNode;
  /** Tooltip + accessible label. */
  tip: string;
  /** Engaged / selected state — border + fg accent. */
  active?: boolean;
  /** Non-interactive. */
  disabled?: boolean;
  /** Which accent color to use for the active state. */
  accent?: ToolAccent;
  /** Idle-state styling — defaults to plain; `"cta"` paints a solid accent
   *  call-to-action when `active` is false. */
  emphasis?: ToolEmphasis;
  /** One-shot highlight-on-mount pulse (pair with `useHighlightOnChange`). */
  highlight?: boolean;
  /** Bottom-right corner badges (max 2, small). */
  badges?: ReactNode[];
  /** Primary action. Runs on click (and variant row activation). */
  onActivate?: () => void;
  /** Variants for a grouped tool. Presence turns this into a tool group. */
  variants?: readonly ToolVariant[];
  /** Override the flyout minimum width for this group. */
  flyoutMinWidth?: number;
  /** Raw pointer-down escape hatch (hold-to-repeat, etc.). Ignored when
   *  `variants` is set — that path owns its own drag gestures. */
  onPointerDown?: (e: React.PointerEvent<HTMLButtonElement>) => void;
  /** Raw pointer-up escape hatch. See `onPointerDown`. */
  onPointerUp?: (e: React.PointerEvent<HTMLButtonElement>) => void;
  /** Raw pointer-cancel escape hatch. See `onPointerDown`. */
  onPointerCancel?: (e: React.PointerEvent<HTMLButtonElement>) => void;
  /**
   * Variant-group behaviour override. By default the primary trigger action
   * opens the flyout AND touch/mouse-drag opens it too. Set `triggerMode`
   * to `"activate"` to have click run `onActivate` (e.g. execute the primary
   * operator) instead of opening the flyout — long-press / drag still opens
   * the flyout for variant selection. Used by operator tool groups that
   * want single-click = run-primary, hold = pick-variant.
   */
  triggerMode?: "openFlyout" | "activate";
};

export const Tool = forwardRef<HTMLButtonElement, ToolProps>(function Tool(
  {
    icon,
    tip,
    active,
    disabled,
    accent = "default",
    emphasis = "none",
    highlight,
    badges,
    onActivate,
    variants,
    flyoutMinWidth,
    triggerMode = "openFlyout",
    onPointerDown: userPointerDown,
    onPointerUp: userPointerUp,
    onPointerCancel: userPointerCancel,
  },
  forwardedRef,
) {
  const localRef = useRef<HTMLButtonElement | null>(null);
  const setRef = (node: HTMLButtonElement | null) => {
    localRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };

  const surface = useToolSurface();
  const hasVariants = variants !== undefined && variants.length > 0;
  const [open, setOpen] = useState(false);

  if (highlight) ensureHighlightStyle();

  const drag = useToolGroupDrag({
    enabled: hasVariants,
    setOpen,
    onSelect: (i) => {
      const v = variants?.[i];
      if (!v || v.disabled) return;
      v.onActivate();
      setOpen(false);
    },
  });

  const handleClick = hasVariants
    ? drag.wrapOnClick(() => {
        if (triggerMode === "activate") {
          onActivate?.();
          return;
        }
        setOpen((p) => !p);
      })
    : onActivate;

  const handleKeyDown = hasVariants
    ? (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          setOpen(true);
        }
      }
    : undefined;

  const visuallyActive = active || (hasVariants && open);
  const activeBorderColor =
    accent === "caution" ? COLOR_CAUTION : COLOR_ACCENT;
  const activeBgColor =
    accent === "caution" ? COLOR_CAUTION_BG : COLOR_ACTIVE_BG;
  const isIdleCta = emphasis === "cta" && !visuallyActive && !disabled;
  const bgColor = isIdleCta
    ? COLOR_ACCENT
    : visuallyActive
      ? activeBgColor
      : "transparent";
  const fgColor = isIdleCta
    ? COLOR_TEXT_ON_ACCENT
    : visuallyActive
      ? activeBorderColor
      : disabled
        ? COLOR_TEXT_DISABLED
        : COLOR_TEXT_FAINT;
  const hasBadges = badges != null && badges.length > 0;

  return (
    <>
      <button
        ref={setRef}
        onClick={disabled ? undefined : handleClick}
        onKeyDown={handleKeyDown}
        onPointerDown={hasVariants ? drag.onPointerDown : userPointerDown}
        onPointerMove={hasVariants ? drag.onPointerMove : undefined}
        onPointerUp={hasVariants ? drag.onPointerUp : userPointerUp}
        onPointerCancel={
          hasVariants ? drag.onPointerCancel : userPointerCancel
        }
        title={tip}
        aria-label={tip}
        aria-haspopup={hasVariants ? "menu" : undefined}
        aria-expanded={hasVariants ? open : undefined}
        disabled={disabled}
        style={{
          width: TOOL_SIZE,
          height: TOOL_SIZE,
          borderRadius: RADIUS_SM,
          border: visuallyActive
            ? `1.5px solid ${activeBorderColor}`
            : "1.5px solid transparent",
          background: bgColor,
          color: fgColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "default" : "pointer",
          padding: 0,
          opacity: disabled ? 0.5 : visuallyActive ? 1 : 0.8,
          flexShrink: 0,
          position: "relative",
          touchAction: hasVariants ? "none" : undefined,
          transition:
            "border-color 0.15s ease, color 0.15s ease, background 0.15s ease",
          ...(highlight ? { animation: HIGHLIGHT_BTN_ANIMATION } : undefined),
        }}
      >
        {icon}
        {hasVariants && !hasBadges && (
          <CaretIndicator
            color={
              visuallyActive
                ? activeBorderColor
                : disabled
                  ? COLOR_TEXT_DISABLED
                  : COLOR_TEXT_DIM
            }
          />
        )}
        {hasBadges && <Badges badges={badges!} />}
      </button>
      {hasVariants && (
        <ToolFlyout
          anchorRef={localRef}
          open={open}
          onClose={() => setOpen(false)}
          variants={variants!}
          placement={flyoutPlacementForEdge(surface?.edge)}
          dragHighlightIndex={drag.dragHighlightIndex}
          minWidth={flyoutMinWidth}
        />
      )}
    </>
  );
});

function CaretIndicator({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      style={{
        position: "absolute",
        bottom: 2,
        right: 2,
        width: 0,
        height: 0,
        borderLeft: "3px solid transparent",
        borderRight: "3px solid transparent",
        borderTop: `3px solid ${color}`,
        opacity: 0.6,
      }}
    />
  );
}

function Badges({ badges }: { badges: ReactNode[] }) {
  return (
    <span
      style={{
        position: "absolute",
        bottom: 0,
        right: 0,
        display: "flex",
        flexDirection: "row-reverse",
        alignItems: "center",
        gap: 2,
        pointerEvents: "none",
      }}
    >
      {badges.slice(0, 2).map((badge, i) => (
        <span
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 14,
            height: 14,
            borderRadius: 3,
            background: COLOR_HEADER_BG,
            color: COLOR_ACCENT,
            fontSize: 9,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {badge}
        </span>
      ))}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Highlight animation — lazy-injected singleton stylesheet, driven by the
// `highlight` prop (typically gated through `useHighlightOnChange`).
// ---------------------------------------------------------------------------

const HIGHLIGHT_KEYFRAME_NAME = "tool-highlight-pulse";
const HIGHLIGHT_BTN_ANIMATION = `${HIGHLIGHT_KEYFRAME_NAME} ${TOOLBAR_HIGHLIGHT_DURATION_MS}ms ease-in-out`;

let _highlightStyleInjected = false;
function ensureHighlightStyle() {
  if (_highlightStyleInjected) return;
  const style = document.createElement("style");
  style.textContent = `@keyframes ${HIGHLIGHT_KEYFRAME_NAME} {
  0%   { box-shadow: inset 0 0 0 50px transparent; }
  12%  { box-shadow: inset 0 0 0 50px ${COLOR_TOOLBAR_HIGHLIGHT}; }
  100% { box-shadow: inset 0 0 0 50px transparent; }
}`;
  document.head.appendChild(style);
  _highlightStyleInjected = true;
}
